import { useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import { businessType } from '@makila/pos-core';
import { BoError, type Session, signup } from '../api';
import { Alert, Button, Field, Input, NuitInput, PhoneInput, Select, Toggle } from '../ui';
import { BusinessPicker, type BusinessChoice, choicePayload, emptyChoice } from './BusinessPicker';

export const PROVINCES = ['Maputo Cidade', 'Maputo Província', 'Gaza', 'Inhambane', 'Sofala', 'Manica', 'Tete', 'Zambézia', 'Nampula', 'Cabo Delgado', 'Niassa'] as const;
export const VAT_REGIMES = [
  { value: 'normal', label: 'Regime normal', hint: 'Cobra IVA e entrega a declaração mensal' },
  { value: 'simplified', label: 'Regime simplificado', hint: 'Pequenos negócios abaixo do limite do regime simplificado' },
  { value: 'exempt', label: 'Isento', hint: 'Não cobra IVA (art. 9 do CIVA)' },
  { value: 'ispc', label: 'ISPC', hint: 'Imposto Simplificado para Pequenos Contribuintes' },
] as const;

const STEPS = ['Conta', 'Empresa', 'Negócio', 'Arranque'] as const;

function strength(p: string): { score: 0 | 1 | 2 | 3; label: string } {
  let s = 0;
  if (p.length >= 10) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) s++;
  const score = (p.length < 10 ? 0 : Math.min(3, s)) as 0 | 1 | 2 | 3;
  return { score, label: ['Curta demais', 'Razoável', 'Boa', 'Forte'][score]! };
}

/**
 * Registo em 4 passos (docs/14 §3): conta → empresa → tipo de negócio →
 * arranque. O sistema prepara-se para o tipo escolhido (módulos, menus,
 * ecrã inicial da caixa e, se quiser, exemplos de artigos ou serviços).
 */
export function Signup({ onSession, onLogin }: { onSession: (s: Session) => void; onLogin: () => void }) {
  const [step, setStep] = useState(0);
  const [acc, setAcc] = useState({ name: '', email: '', phone: '', password: '' });
  const [co, setCo] = useState({ legalName: '', tradeName: '', nuit: '', vatRegime: 'normal', province: '', city: '', address: '', phone: '' });
  const [biz, setBiz] = useState<BusinessChoice>(emptyChoice);
  const [start, setStart] = useState({ storeName: '', samples: true });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const pw = strength(acc.password);
  const errs: Record<string, string | null>[] = [
    {
      name: acc.name.trim().length < 2 ? 'Indique o seu nome' : null,
      email: /^\S+@\S+\.\S+$/.test(acc.email.trim()) ? null : 'Email inválido',
      password: acc.password.length < 10 ? 'Pelo menos 10 caracteres' : null,
    },
    {
      legalName: co.legalName.trim().length < 2 ? 'Indique a firma (o nome registado)' : null,
      nuit: /^\d{9}$/.test(co.nuit) ? null : 'O NUIT tem 9 algarismos',
      province: co.province ? null : 'Escolha a província',
    },
    { type: biz.type ? null : 'Escolha o tipo de negócio' },
    {},
  ];
  const stepErr = (k: string) => (touched ? errs[step]![k] ?? null : null);
  const valid = (i: number) => Object.values(errs[i]!).every((x) => !x);

  const next = () => {
    setTouched(true);
    if (!valid(step)) return;
    setTouched(false);
    setError(null);
    setStep(step + 1);
    scrollTo({ top: 0 });
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const b = choicePayload(biz);
      onSession(await signup({
        name: acc.name.trim(), email: acc.email.trim(), password: acc.password,
        company: {
          legalName: co.legalName.trim(), tradeName: co.tradeName.trim() || undefined, nuit: co.nuit, vatRegime: co.vatRegime, businessType: b.businessType,
          province: co.province, city: co.city.trim() || undefined, address: co.address.trim() || undefined, phone: (co.phone || acc.phone).trim() || undefined,
        },
        profile: b.profile, modules: b.modules, samples: start.samples, storeName: start.storeName.trim() || undefined,
      }));
    } catch (e) {
      setError(e instanceof BoError ? e.message : 'Sem ligação à internet.');
    } finally { setBusy(false); }
  };

  const type = biz.type ? businessType(biz.type) : null;

  return (
    <div className="min-h-full bg-[var(--bg)] px-4 py-6 sm:py-10">
      <div className="mx-auto w-full max-w-[640px]">
        <div className="mb-6 flex items-center gap-3">
          <img src="/makila-simbolo.svg" alt="" className="h-10 w-10" />
          <div>
            <h1 className="font-display text-xl font-bold">Registar a empresa</h1>
            <p className="text-sm text-muted">14 dias de teste com tudo, em modo de formação</p>
          </div>
        </div>

        {/* Progresso */}
        <ol className="mb-6 grid grid-cols-4 gap-2" aria-label="Passos do registo">
          {STEPS.map((s, i) => (
            <li key={s} aria-current={i === step ? 'step' : undefined} className="min-w-0">
              <div className={clsx('h-1.5 rounded-full', i <= step ? 'bg-brand-700' : 'bg-[var(--border)]')} />
              <div className={clsx('mt-1.5 flex items-center gap-1 truncate text-xs font-medium', i === step ? 'text-[var(--text)]' : 'text-muted')}>
                {i < step && <Check size={12} className="shrink-0 text-green-600" />}{i + 1}. {s}
              </div>
            </li>
          ))}
        </ol>

        <form onSubmit={(e) => { e.preventDefault(); if (step < 3) next(); else void submit(); }} noValidate className="surface rounded-2xl p-5 shadow-xs sm:p-7">
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="font-display text-lg font-semibold">A sua conta</h2>
              <Field label="O seu nome" required error={stepErr('name')}><Input autoFocus autoComplete="name" value={acc.name} onChange={(e) => setAcc({ ...acc, name: e.target.value })} /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" required error={stepErr('email')} hint="É com ele que entra na gestão"><Input type="email" autoComplete="email" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} /></Field>
                <Field label="Telefone" hint="Para o contactarmos se for preciso"><PhoneInput className="w-full" value={acc.phone} onChange={(v) => setAcc({ ...acc, phone: v })} /></Field>
              </div>
              <Field label="Palavra-passe" required error={stepErr('password')} hint="Pelo menos 10 caracteres">
                <div className="relative">
                  <Input type={show ? 'text' : 'password'} autoComplete="new-password" minLength={10} value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} className="pr-12" />
                  <button type="button" aria-label={show ? 'Esconder a palavra-passe' : 'Mostrar a palavra-passe'} onClick={() => setShow(!show)} className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-muted hover:bg-surface-2">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
              </Field>
              {acc.password && (
                <div aria-live="polite" className="flex items-center gap-2 text-xs text-muted">
                  <div className="grid flex-1 grid-cols-3 gap-1">{[1, 2, 3].map((i) => <span key={i} className={clsx('h-1 rounded-full', pw.score >= i ? (pw.score === 1 ? 'bg-amber-500' : 'bg-green-600') : 'bg-[var(--border)]')} />)}</div>
                  {pw.label}
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display text-lg font-semibold">A empresa</h2>
              <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                <Field label="Firma" required error={stepErr('legalName')} hint="O nome registado, como na certidão"><Input autoFocus autoComplete="organization" value={co.legalName} onChange={(e) => setCo({ ...co, legalName: e.target.value })} /></Field>
                <Field label="NUIT" required error={stepErr('nuit')}><NuitInput value={co.nuit} onChange={(v) => setCo({ ...co, nuit: v })} /></Field>
              </div>
              <Field label="Nome comercial" hint="O nome que os clientes conhecem e que sai no talão (opcional)"><Input value={co.tradeName} onChange={(e) => setCo({ ...co, tradeName: e.target.value })} placeholder={co.legalName || 'Ex.: Barbearia Estilo'} /></Field>
              <Field label="Regime de IVA" hint={VAT_REGIMES.find((v) => v.value === co.vatRegime)?.hint}>
                <Select value={co.vatRegime} onChange={(e) => setCo({ ...co, vatRegime: e.target.value })}>
                  {VAT_REGIMES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </Select>
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Província" required error={stepErr('province')}>
                  <Select value={co.province} onChange={(e) => setCo({ ...co, province: e.target.value })}>
                    <option value="">— escolher —</option>
                    {PROVINCES.map((p) => <option key={p}>{p}</option>)}
                  </Select>
                </Field>
                <Field label="Cidade ou distrito"><Input value={co.city} onChange={(e) => setCo({ ...co, city: e.target.value })} /></Field>
              </div>
              <Field label="Morada" hint="Rua, número, bairro — sai nos documentos"><Input autoComplete="street-address" value={co.address} onChange={(e) => setCo({ ...co, address: e.target.value })} /></Field>
              <Field label="Telefone da empresa" hint="Se for diferente do seu"><PhoneInput value={co.phone} onChange={(v) => setCo({ ...co, phone: v })} /></Field>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="mb-1 font-display text-lg font-semibold">O seu negócio</h2>
              <p className="mb-5 text-sm text-muted">O sistema adapta-se: menus, ecrãs da caixa e campos de cada artigo.</p>
              <BusinessPicker value={biz} onChange={setBiz} />
              {stepErr('type') && <p role="alert" className="mt-4 text-sm text-brand-700">{stepErr('type')}</p>}
            </div>
          )}

          {step === 3 && type && (
            <div className="space-y-4">
              <h2 className="font-display text-lg font-semibold">Arranque</h2>
              <Field label="Nome da primeira loja" hint="Pode criar mais lojas depois"><Input value={start.storeName} onChange={(e) => setStart({ ...start, storeName: e.target.value })} placeholder={co.tradeName || co.legalName} /></Field>
              {type.samples.length > 0 && (
                <Toggle checked={start.samples} onChange={(v) => setStart({ ...start, samples: v })}
                  label={`Criar exemplos de ${type.name.toLowerCase()}`}
                  description={`${type.samples.map((c) => c.name).join(', ')} — ${type.samples.reduce((n, c) => n + c.items.length, 0)} ${type.terms.item === 'Serviço' ? 'serviços e produtos' : 'artigos'} com preços de referência, para corrigir. Assim a caixa não começa vazia.`} />
              )}
              <Alert tone="info">
                A seguir: criar os {type.terms.staffPlural.toLowerCase()} com o PIN, activar a caixa e fazer uma venda de teste. O Painel mostra-lhe os passos.
              </Alert>
              {error && <Alert>{error}</Alert>}
            </div>
          )}

          <div className="mt-7 flex items-center justify-between gap-3 border-t border-base pt-5">
            {step > 0
              ? <Button type="button" variant="subtle" icon={ArrowLeft} onClick={() => { setStep(step - 1); setTouched(false); }}>Voltar</Button>
              : <button type="button" onClick={onLogin} className="text-sm font-medium text-brand-700 hover:underline">Já tem conta? Entrar</button>}
            {step < 3
              ? <Button type="submit">Continuar <ArrowRight size={18} /></Button>
              : <Button type="submit" size="lg" disabled={busy}>{busy ? 'A preparar…' : 'Começar'}</Button>}
          </div>
        </form>
      </div>
    </div>
  );
}
